import { createRef, useCallback, useMemo, useState } from 'react'
import { times } from 'lodash'

import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponse } from '@/utils/sharedHooks'
import {
  fetchEmployeeStateTaxes,
  PUT_UPDATE_EMPLOYEE_STATE_TAXES_KEY,
  setupFinishEmployeeSetup,
} from '../payrollActions'
import {
  StateTaxSection,
  StateTaxSectionHandle,
} from '../enroll/PayrollEmployeeState'
import { Button, Dialog } from '@/ui'
import { PayrollError } from '../PayrollError'

export const OnboardEmployeeModal = ({
  employeeUuid,
  open,
  onClose,
}: {
  employeeUuid: string
  open: boolean
  onClose: () => void
}) => {
  const dispatch = useAppDispatch()
  const [submitting, setSubmitting] = useState(false)

  const stateTaxes = useFetchResponse(fetchEmployeeStateTaxes, employeeUuid)

  const stateSectionRefs = useMemo(
    () =>
      times(stateTaxes?.length || 0).map(() =>
        createRef<StateTaxSectionHandle>()
      ),
    [stateTaxes?.length]
  )

  const onboardEmployee = useCallback(async () => {
    setSubmitting(true)

    const statePayload = {
      states: filterNulls(
        stateSectionRefs.map((section) => section.current?.getQuestionAnswers())
      ),
    }

    const res = await dispatch(
      setupFinishEmployeeSetup(employeeUuid, statePayload)
    )

    setSubmitting(false)

    if (res) {
      onClose()
    }
  }, [dispatch, employeeUuid, onClose, stateSectionRefs])

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="max-w-sm">
        <Dialog.Title>Onboard Employee</Dialog.Title>
        <div className="mt-2 space-y-4">
          <PayrollError fetchKey={PUT_UPDATE_EMPLOYEE_STATE_TAXES_KEY} />

          {stateTaxes?.map((taxesForSingleState, index) => {
            if (!stateSectionRefs[index]) return null
            return (
              <StateTaxSection
                key={taxesForSingleState.state}
                ref={stateSectionRefs[index]}
                taxesForSingleState={taxesForSingleState}
              />
            )
          })}
        </div>

        <Dialog.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onboardEmployee}
            disabled={submitting}
            loading={submitting}
          >
            Onboard Employee
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
